// ======================================================================
// 📁 File: src/modules/expense_management/layout/ExpenseManagementSidebar.jsx
// 📝 Purpose:
//   Sidebar navigation for Expense Management module
//   - RBAC-driven
//   - Collapsible sidebar
//   - ERP-standard grouped navigation
//   - Follows Service CRM architecture
// ======================================================================

import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";

// ----------------------------------------------------------------------
// Permission Helper
// ----------------------------------------------------------------------

const canAny = (permissions, keys = []) =>
    keys.some((k) => permissions?.[k]?.view === true);

// ----------------------------------------------------------------------
// Sidebar Menu Definition
// ----------------------------------------------------------------------

const MENU = [

    // ==========================================================
    // DASHBOARD
    // ==========================================================

    {
        label: "Dashboard",
        route: "/expense-management/dashboard",
        perms: ["EXP_DASHBOARD"],
        icon: "fas fa-chart-pie",
    },

    // ==========================================================
    // OPERATIONS
    // ==========================================================

    {
        type: "group",
        label: "Operations",
    },

    {
        label: "My Expenses",
        route: "/expense-management/my-expenses",
        perms: ["EXP_MY_EXPENSES"],
        icon: "fas fa-wallet",
    },

    {
        label: "Expense Claims",
        route: "/expense-management/claims",
        perms: ["EXP_EXPENSE_CLAIMS"],
        icon: "fas fa-file-invoice-dollar",
    },

    {
        label: "Expense Advances",
        route: "/expense-management/advances",
        perms: ["EXP_EXPENSE_ADVANCES"],
        icon: "fas fa-hand-holding-usd",
    },

    {
        label: "Expense Approvals",
        route: "/expense-management/approvals",
        perms: ["EXP_EXPENSE_APPROVALS"],
        icon: "fas fa-user-check",
    },

    {
        label: "Expense Payments",
        route: "/expense-management/payments",
        perms: ["EXP_EXPENSE_PAYMENTS"],
        icon: "fas fa-money-check-alt",
    },

    {
        label: "Expense Settlements",
        route: "/expense-management/settlements",
        perms: ["EXP_EXPENSE_SETTLEMENTS"],
        icon: "fas fa-file-signature",
    },

    // ==========================================================
    // REPORTS
    // ==========================================================

    {
        type: "group",
        label: "Reports",
    },

    {
        label: "Expense Register",
        route: "/expense-management/reports/register",
        perms: ["EXP_REPORT_REGISTER"],
        icon: "fas fa-book",
    },

    {
        label: "Expense Claim Report",
        route: "/expense-management/reports/claims",
        perms: ["EXP_REPORT_CLAIMS"],
        icon: "fas fa-file-invoice-dollar",
    },

    {
        label: "Expense Approval Report",
        route: "/expense-management/reports/approvals",
        perms: ["EXP_REPORT_APPROVALS"],
        icon: "fas fa-user-check",
    },

    {
        label: "Expense Payment Report",
        route: "/expense-management/reports/payments",
        perms: ["EXP_REPORT_PAYMENTS"],
        icon: "fas fa-money-check-alt",
    },

    {
        label: "Expense Advance Report",
        route: "/expense-management/reports/advances",
        perms: ["EXP_REPORT_ADVANCES"],
        icon: "fas fa-hand-holding-usd",
    },

    {
        label: "Expense Settlement Report",
        route: "/expense-management/reports/settlements",
        perms: ["EXP_REPORT_SETTLEMENTS"],
        icon: "fas fa-file-signature",
    },

    {
        label: "Mileage Report",
        route: "/expense-management/reports/mileage",
        perms: ["EXP_REPORT_MILEAGE"],
        icon: "fas fa-road",
    },

    {
        label: "Per Diem Report",
        route: "/expense-management/reports/per-diem",
        perms: ["EXP_REPORT_PER_DIEM"],
        icon: "fas fa-map-marked-alt",
    },

    {
        label: "Policy Violation Report",
        route: "/expense-management/reports/policy-violations",
        perms: ["EXP_REPORT_POLICY_VIOLATION"],
        icon: "fas fa-exclamation-triangle",
    },

    // ==========================================================
    // MASTERS
    // ==========================================================

    {
        type: "group",
        label: "Masters",
    },

    {
        label: "Expense Category",
        route: "/expense-management/masters/category",
        perms: ["EXP_CATEGORY"],
        icon: "fas fa-tags",
    },

    {
        label: "Expense Type",
        route: "/expense-management/masters/type",
        perms: ["EXP_TYPE"],
        icon: "fas fa-list",
    },

    {
        label: "Payment Mode",
        route: "/expense-management/masters/payment-mode",
        perms: ["EXP_PAYMENT_MODE"],
        icon: "fas fa-credit-card",
    },

    {
        label: "Currency",
        route: "/expense-management/masters/currency",
        perms: ["EXP_CURRENCY"],
        icon: "fas fa-dollar-sign",
    },

    {
        label: "Mileage Rate",
        route: "/expense-management/masters/mileage-rate",
        perms: ["EXP_MILEAGE_RATE"],
        icon: "fas fa-road",
    },

    {
        label: "Per Diem Rate",
        route: "/expense-management/masters/per-diem-rate",
        perms: ["EXP_PER_DIEM_RATE"],
        icon: "fas fa-map-marked-alt",
    },

    {
        label: "Tax",
        route: "/expense-management/masters/tax",
        perms: ["EXP_TAX"],
        icon: "fas fa-percent",
    },

    {
        label: "Vendor",
        route: "/expense-management/masters/vendor",
        perms: ["EXP_VENDOR"],
        icon: "fas fa-store",
    },

    {
        label: "Cost Center",
        route: "/expense-management/masters/cost-center",
        perms: ["EXP_COST_CENTER"],
        icon: "fas fa-sitemap",
    },

    {
        label: "Project",
        route: "/expense-management/masters/project",
        perms: ["EXP_PROJECT"],
        icon: "fas fa-project-diagram",
    },

    {
        label: "Expense Reason",
        route: "/expense-management/masters/expense-reason",
        perms: ["EXP_EXPENSE_REASON"],
        icon: "fas fa-comment-alt",
    },

    {
        label: "Receipt Type",
        route: "/expense-management/masters/receipt-type",
        perms: ["EXP_RECEIPT_TYPE"],
        icon: "fas fa-receipt",
    },

    {
        label: "Approval Level",
        route: "/expense-management/masters/approval-level",
        perms: ["EXP_APPROVAL_LEVEL"],
        icon: "fas fa-user-check",
    },

    {
        label: "Reimbursement Method",
        route: "/expense-management/masters/reimbursement-method",
        perms: ["EXP_REIMBURSEMENT_METHOD"],
        icon: "fas fa-money-check",
    },

    {
        label: "Expense Policy",
        route: "/expense-management/masters/expense-policy",
        perms: ["EXP_EXPENSE_POLICY"],
        icon: "fas fa-file-contract",
    },

    {
        label: "Expense Policy Rule",
        route: "/expense-management/masters/expense-policy-rule",
        perms: ["EXP_EXPENSE_POLICY_RULE"],
        icon: "fas fa-gavel",
    },

    // ==========================================================
    // CONFIGURATION
    // ==========================================================

    {
        type: "group",
        label: "Configuration",
    },

    {
        label: "Approval Workflow",
        route: "/expense-management/configuration/approval-workflow",
        perms: ["EXP_APPROVAL_WORKFLOW"],
        icon: "fas fa-project-diagram",
    },

    {
        label: "Approval Rules",
        route: "/expense-management/configuration/approval-rules",
        perms: ["EXP_APPROVAL_RULES"],
        icon: "fas fa-user-check",
    },

    {
        label: "Number Series",
        route: "/expense-management/configuration/number-series",
        perms: ["EXP_NUMBER_SERIES"],
        icon: "fas fa-sort-numeric-up",
    },

    {
        label: "Expense Settings",
        route: "/expense-management/configuration/settings",
        perms: ["EXP_SETTINGS"],
        icon: "fas fa-cogs",
    },

    {
        label: "Notification Rules",
        route: "/expense-management/configuration/notification-rules",
        perms: ["EXP_NOTIFICATION_RULES"],
        icon: "fas fa-bell",
    },

    {
        label: "Policy Configuration",
        route: "/expense-management/configuration/policy",
        perms: ["EXP_POLICY_CONFIGURATION"],
        icon: "fas fa-file-contract",
    },

];
// ======================================================================
// Sidebar Component
// ======================================================================

export default function ExpenseManagementSidebar({ onToggle }) {

  const { permissions } = useContext(AuthContext);
  const location = useLocation();

  const collapsedLS = localStorage.getItem(
    "expense_management_sidebar_collapsed"
  );

  const [expanded, setExpanded] = useState(
    collapsedLS === null ? true : collapsedLS === "false"
  );

  useEffect(() => {

    localStorage.setItem(
      "expense_management_sidebar_collapsed",
      expanded ? "false" : "true"
    );

    onToggle?.(expanded);

  }, [expanded, onToggle]);

  return (

    <aside
      style={{
        width: expanded ? 260 : 64,
        transition: "width 0.22s ease",
        background: "#111827",
        color: "#fff",
        minHeight: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
      }}
    >

      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}

      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          padding: expanded ? "0 12px" : "0",
          borderBottom: "1px solid #374151",
        }}
      >

        {expanded && (

          <span
            style={{
              fontWeight: 700,
            }}
          >
            Expense Management
          </span>

        )}

        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
          }}
        >
          <i className="fas fa-bars" />
        </button>

      </div>

      {/* ====================================================== */}
      {/* Back to Dashboard */}
      {/* ====================================================== */}

      <Link
        to="/dashboard"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: 12,
          padding: "10px 12px",
          borderRadius: 999,
          background: "#2563eb",
          color: "#fff",
          textDecoration: "none",
          justifyContent: expanded
            ? "flex-start"
            : "center",
        }}
      >

        <i className="fas fa-home" />

        {expanded && (
          <span>
            Back to Dashboard
          </span>
        )}

      </Link>

      {/* ====================================================== */}
      {/* Menu */}
      {/* ====================================================== */}

      <nav
        style={{
          maxHeight: "calc(100vh - 140px)",
          overflowY: "auto",
        }}
      >
                {MENU.map((item, idx) => {

          // =====================================================
          // GROUP HEADER
          // =====================================================

          if (item.type === "group") {

            if (!expanded) return null;

            return (
              <div
                key={`grp-${idx}`}
                style={{
                  margin: "12px 14px 6px",
                  fontSize: 11,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </div>
            );
          }

          // =====================================================
          // RBAC
          // =====================================================

          if (!canAny(permissions, item.perms)) {
            return null;
          }

          // =====================================================
          // ACTIVE MENU
          // =====================================================

          const active = location.pathname.startsWith(item.route);

          return (

            <NavLink
              key={item.route}
              to={item.route}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: expanded
                  ? "10px 14px"
                  : "10px 0",
                justifyContent: expanded
                  ? "flex-start"
                  : "center",
                color: active
                  ? "#fff"
                  : "#cbd5e1",
                background: active
                  ? "#374151"
                  : "transparent",
                textDecoration: "none",
                fontSize: 14,
              }}
            >

              <i className={item.icon} />

              {expanded && (
                <span>
                  {item.label}
                </span>
              )}

            </NavLink>

          );

        })}

      </nav>

    </aside>

  );

}